<template>
  <div class="view">
    <v-breadcrumbs>
      <v-breadcrumbs-item :exact="true" :to="{ name: 'home', params: {} }"
        >Home</v-breadcrumbs-item
      >
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item
        :exact="true"
        :to="{ name: 'studentRequirementSubmission', params: {} }"
      >
        All Student Submissions
      </v-breadcrumbs-item>
      <v-breadcrumbs-divider v-if="studentRequirementSubmission.studentId">/</v-breadcrumbs-divider>
      <v-breadcrumbs-item
        v-if="studentRequirementSubmission.studentId"
        :exact="true"
        :to="{ name: 'studentView', params: { id: studentRequirementSubmission.studentId } }"
      >
        {{ studentRequirementSubmission.studentName }}
      </v-breadcrumbs-item>
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item
        :to="{ name: 'studentRequirementSubmissionView', params: {} }"
      >
        Student Submission
      </v-breadcrumbs-item>
    </v-breadcrumbs>

    <v-card v-if="loaded">
      <v-card-title>
        Student
        {{
          studentRequirementSubmission.waived
            ? "Requirement Waiver"
            : "Submission"
        }}
      </v-card-title>
      <v-card-text>
        <v-alert
          dense
          type="warning"
          v-if="
            studentRequirementSubmission.state == 'pending' &&
            studentRequirementSubmission.formSubmissionId
          "
        >
          This online submission is pending review
        </v-alert>
        <v-simple-table>
          <template v-slot:default>
            <tbody>
              <tr v-if="studentRequirementSubmission.studentId">
                <td max-width="35%" class="text-subtitle-1">Student:</td>
                <td>
                  <router-link
                    :to="{
                      name: 'studentView',
                      params: { id: studentRequirementSubmission.studentId },
                    }"
                  >
                    {{ studentRequirementSubmission.studentName }}
                  </router-link>
                </td>
              </tr>

              <tr>
                <td max-width="35%" class="text-subtitle-1">Submission:</td>
                <td>
                  <div v-if="studentRequirementSubmission.waived">
                    Waived by
                    {{ studentRequirementSubmission.approvedByName }} on
                    {{
                      studentRequirementSubmission.approvedOn | formatDateYear
                    }}
                  </div>
                  <div v-else>
                    <div v-if="studentRequirementSubmission.createdByName">
                      By {{ studentRequirementSubmission.createdByName }} on
                      {{
                        studentRequirementSubmission.createdAt | formatDateYear
                      }}
                      <span v-if="studentRequirementSubmission.submissionMethod"
                        >via
                        {{
                          studentRequirementSubmission.submissionMethod
                        }}</span
                      >
                    </div>
                    <div
                      class="my-2"
                      v-if="studentRequirementSubmission.approvedBy"
                    >
                      Approved by
                      {{ studentRequirementSubmission.approvedByName }} on
                      {{
                        studentRequirementSubmission.approvedOn | formatDateYear
                      }}
                    </div>
                  </div>
                </td>
              </tr>

              <tr v-if="studentRequirementSubmission.notes">
                <td max-width="35%" class="text-subtitle-1">
                  <span v-if="studentRequirementSubmission.waived"
                    >Waiver Reason:</span
                  >
                  <span v-else>Notes:</span>
                </td>
                <td>{{ studentRequirementSubmission.notes }}</td>
              </tr>

              <tr v-if="studentRequirementSubmission.formSubmissionId">
                <td max-width="35%" class="text-subtitle-1">
                  Online Form Submission:
                </td>
                <td>
                  <div class="d-flex justify-space-between">
                    <form-response
                      class="my-auto"
                      :responseId="
                        studentRequirementSubmission.formSubmissionId
                      "
                    />
                    <v-btn
                      @click="approvePending"
                      text
                      class="my-2"
                      v-if="studentRequirementSubmission.state == 'pending'"
                      >Approve</v-btn
                    >
                  </div>
                </td>
              </tr>

              <tr v-if="studentRequirementSubmission.requirements">
                <td max-width="35%" class="text-subtitle-1">Requirements:</td>
                <td>
                  <div class="my-2">
                    <div
                      class="
                        my-1
                        d-flex
                        justify-space-between
                        flex-column flex-md-row
                      "
                      v-for="(requirement, index) in requirementsSorted"
                      :key="`${requirement.requirementId}-${requirement.forDoseNumber}`"
                    >
                      <span
                        ><span
                          v-if="
                            studentRequirementSubmission.requirements.length > 1
                          "
                          >{{ index + 1 }}.</span
                        >
                        {{ requirement.requirementName }}</span
                      >
                      <span
                        class="ml-4 font-weight-light"
                        v-if="
                          requirement.cerfIssueDate &&
                          requirement.cerfExpireDate
                        "
                      >
                        {{ requirement.cerfIssueDate | formatDateYear }} -
                        {{ requirement.cerfExpireDate | formatDateYear }}
                      </span>
                      <span
                        class="ml-4 font-weight-light"
                        v-if="
                          requirement.type == 'Vaccination' &&
                          requirement.forDoseNumber
                        "
                      >
                        Dose {{ requirement.forDoseNumber }}
                        <span v-if="!studentRequirementSubmission.waived">
                          on
                          {{ requirement.vaccinationDate | formatDateYear }}
                        </span>
                      </span>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <attachments-view v-model="studentRequirementSubmission.attachments" />
      </v-card-text>
    </v-card>

    <div class="d-flex justify-space-between ma-5">
      <v-btn @click="goBack">Back</v-btn>
      <v-btn v-if="!studentRequirementSubmission.waived" @click="edit(id)" color="primary">Edit</v-btn>
      <v-btn @click="remove(id)" color="warning">Delete</v-btn>
    </div>

    <confirmation-dialog ref="confirm" />
  </div>
</template>

<script>
import { config } from '@/config'
import { mapActions } from "vuex";
import { getStudentRequirementSubmission } from "@/services/studentRequirementSubmission";
const AttachmentsView = () => import("@/components/common/AttachmentsView.vue");
const FormResponse = () => import("@/components/forms/FormResponse.vue");
const ConfirmationDialog = () =>
  import("@/components/common/ConfirmationDialog.vue");

export default {
  components: { AttachmentsView, FormResponse, ConfirmationDialog },
  data() {
    return {
      id: "",
      studentRequirementSubmission: {},
      loaded: false,
      relations: {},
    };
  },
  computed: {
    requirementsSorted() {
      if (
        this.studentRequirementSubmission &&
        this.studentRequirementSubmission.requirements
      ) {
        const requirements = this.studentRequirementSubmission.requirements;
        requirements.sort((a, b) =>
          a.requirementName.localeCompare(b.requirementName)
        );
        return requirements;
      } else {
        return [];
      }
    },
  },
  methods: {
    ...mapActions("messages", ["addErrorMessage", "addSuccessMessage"]),
    ...mapActions("studentRequirementSubmission", [
      "deleteStudentRequirementSubmission",
    ]),
    goBack() {
      this.$router.go(-1);
    },
    edit(id) {
      this.$router.push({
        name: "studentRequirementSubmissionEdit",
        params: { id: id },
      });
    },
    setInstance() {
      getStudentRequirementSubmission(this.id)
        .then((response) => {
          this.studentRequirementSubmission = response.data;
          if (this.studentRequirementSubmission.attachments) {
            this.studentRequirementSubmission.attachments.forEach((file) => {
              const path = `${config.BASE_URL}/compliance/submission/attachment/${file.id}`;
              file.path = path;
            });
          }

          this.loaded = true;
        })
        .catch(() => {
          this.addErrorMessage("Error loading student submission");
          this.loaded = true;
        });
    },
    getRelationText(id, relationProperty, relationAttribute) {
      const relationList = this.relations[relationProperty];
      if (relationList) {
        const relationObj = relationList.find((r) => r.id == id);
        return relationObj[relationAttribute];
      } else {
        return "";
      }
    },
    approvePending() {
      this.$axios
        .put(
          "/studentRequirementSubmission/approve-pending/" +
            this.studentRequirementSubmission.id
        )
        .then((response) => {
          const approved = response.data;
          this.studentRequirementSubmission.state = approved.state;
          this.studentRequirementSubmission.approvedBy = approved.approvedBy;
          this.studentRequirementSubmission.approvedOn = approved.approvedOn;
        });
    },
    async remove(id) {
      let confirm = await this.$refs.confirm.open(
        "Confirm Delete",
        "Do you want to delete this submission?"
      );
      if (confirm) {
        this.deleteStudentRequirementSubmission(id)
          .then(() => {
            this.addSuccessMessage("Submission was deleted successfully");
            this.goBack();
          })
          .catch(() => {
            this.addErrorMessage("Server response with error ");
          });
      }
    },
  },
  created() {
    this.id = this.$route.params.id;
    this.setInstance();
    this.$emit("showParent", false);
  },
};
</script>
